import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { serverURL } from "../../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// THEME STYLES
// ─────────────────────────────────────────────────────────────────────────────
const getThemeStyles = (isDark) => ({
  dark: {
    background: "#0a0c10",
    surface: "#11131a",
    surfaceLighter: "#1a1d2e",
    surfaceLightest: "#22253a",
    border: "#1f2335",
    borderLight: "#2a2f45",
    text: "#f1f5f9",
    textSecondary: "#cbd5e1",
    textMuted: "#94a3b8",
    textDim: "#64748b",
    placeholder: "#4a4f6e",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    primaryLight: "#a5b4fc",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
    gradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    glass: "rgba(30, 34, 58, 0.8)",
    cardBg: "#141824",
    inputBg: "#0f1520",
  },
  light: {
    background: "#f3f4f6",
    surface: "#ffffff",
    surfaceLighter: "#f8fafc",
    surfaceLightest: "#f3f4f6",
    border: "#e2e8f0",
    borderLight: "#cbd5e1",
    text: "#0f172a",
    textSecondary: "#334155",
    textMuted: "#64748b",
    textDim: "#94a3b8",
    placeholder: "#9ca3af",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    primaryLight: "#818cf8",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
    gradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    glass: "rgba(255, 255, 255, 0.9)",
    cardBg: "#ffffff",
    inputBg: "#ffffff",
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 3v12m0 0-3-3m3 3 3-3M5 21h14" />
    <rect x="3" y="15" width="18" height="5" rx="1" />
  </svg>
);

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// DOCUMENT IMAGE PREVIEW COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const DocumentPreview = ({ picName, label, theme }) => {
  const [imageError, setImageError] = useState(false);
  
  const getCleanFilename = (picPath) => {
    if (!picPath || picPath.trim() === "" || picPath === "NULL" || picPath === "null") return null;
    if (picPath.includes('/')) return picPath.split('/').pop();
    return picPath;
  };
  
  const filename = getCleanFilename(picName);
  const imageUrl = filename ? `${serverURL}/public/uploadnew/upload/${filename}` : null;
  
  console.log(`📷 [${label}] Image URL:`, imageUrl);
  
  if (!imageUrl || imageError) {
    return (
      <div style={{
        width: "80px",
        height: "80px",
        borderRadius: "10px",
        background: theme === 'dark' ? "#1a2035" : "#f3f4f6",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${theme === 'dark' ? "#2a3145" : "#e5e7eb"}`,
        gap: "4px"
      }}>
        <ImageIcon style={{ color: "#64748b", width: "24px", height: "24px" }} />
        <span style={{ fontSize: "8px", color: "#64748b" }}>No Image</span>
      </div>
    );
  }
  
  return (
    <img
      src={imageUrl}
      alt={label}
      onError={() => {
        console.log(`❌ Failed to load image: ${imageUrl}`);
        setImageError(true);
      }}
      style={{
        width: "80px",
        height: "80px",
        borderRadius: "10px",
        objectFit: "cover",
        border: `1px solid ${theme === 'dark' ? "#2a3145" : "#e5e7eb"}`,
        background: theme === 'dark' ? "#0f1520" : "#f8fafc"
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT - Add New Driver
// ─────────────────────────────────────────────────────────────────────────────
const AddNewDriver = ({ isDark = true, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const t = getThemeStyles(isDark);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeSection, setActiveSection] = useState("contact");
  
  // ─────────────────────────────────────────────────────────────────────────────
  // FORM DATA - MATCHING BACKEND FIELD NAMES EXACTLY
  // ─────────────────────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    id: "",
    old_id: "",
    name: "",
    first_name: "",
    last_name: "",
    pre: "",
    gender: "",
    contact: "",
    primary_number: "",
    secondary_number: "",
    whatsapp_number: "",
    email: "",
    password: "",
    address: "",
    permanent_address: "",
    city: "",
    state: "",
    postal: "",
    latitude: "",
    longitude: "",
    date_of_birth: "",
    blood_group: "",
    language: "",
    father_name: "",
    heighest_education: "",
    
    // Bank Details
    bank_name: "",
    bank_branch: "",
    account_holder: "",
    account_number: "",
    ifsc: "",
    bank_mobile: "",
    upi: "",
    pan_card: "",
    
    // Work & Duty
    status: "",
    paid: "",
    open_status: "",
    is_ready: "",
    hide: "",
    wallet: "",
    cash_inhand: "",
    cash_limit: "",
    cash_limit_cheque: "",
    is_cash_limit_cheque: "",
    tshirt_size: "",
    tshirt_given: "",
    bag_given: "",
    starting_point: "",
    vl_register_number: "",
    dl_number: "",
    
    // Verification
    is_verify_personalinfo: "",
    is_verify_docinfo: "",
    is_verify_vehicle: "",
    is_verify_bankinfo: "",
    is_verify_emergency: "",
    remarks_personalinfo: "",
    remarks_docinfo: "",
    remarks_vehicleinfo: "",
    remarks_bankinfo: "",
    remarks_emergencyinfo: "",
    
    // Family/Emergency
    family_number_one: "",
    family_number_two: "",
    
    // Meta
    subject: "",
    details: "",
    refer_by: "",
    myreferal: "",
    created_by_id: "",
    created_at: "",
    updated_at: "",
    rd: "",
    sessionid: "",
    login_session: "",
    fcm_token: "",
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // DOCUMENTS STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [documents, setDocuments] = useState({
    pic: null,
    pic_existing: "",
    aadhar_front: null,
    aadhar_front_existing: "",
    aadhar_back: null,
    aadhar_back_existing: "",
    pan_card: null,
    pan_card_existing: "",
    bank_pass: null,
    bank_pass_existing: "",
    cancel_cheque: null,
    cancel_cheque_existing: "",
    dl_front: null,
    dl_front_existing: "",
    dl_back: null,
    dl_back_existing: "",
    rc_front: null,
    rc_front_existing: "",
    rc_back: null,
    rc_back_existing: "",
    resume: null,
    resume_existing: "",
  });

  const [filledSections, setFilledSections] = useState({
    contact: false,
    personalInfo: false,
    documents: false,
    bankDetails: false,
    workDuty: false,
    verification: false,
    familyEmergency: false,
    meta: false,
  });

  const sections = [
    { id: "contact", label: "Contact", icon: "📞" },
    { id: "personalInfo", label: "Personal Info", icon: "👤" },
    { id: "documents", label: "Documents", icon: "📄" },
    { id: "bankDetails", label: "Bank Details", icon: "🏦" },
    { id: "workDuty", label: "Work & Duty", icon: "⚙️" },
    { id: "verification", label: "Verification", icon: "✅" },
    { id: "familyEmergency", label: "Family/Emergency", icon: "👨‍👩‍👧" },
    { id: "meta", label: "Meta", icon: "ℹ️" },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD EDIT DATA
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const driverData = location.state?.driverData;
    const mode = location.state?.mode;
    
    if (mode === "edit" && driverData) {
      setIsEditMode(true);
      setEditId(driverData.id || driverData.old_id);
      
      console.log("🔄 Loading driver data for edit:", driverData);
      
      setFormData({
        id: driverData.id || "",
        old_id: driverData.old_id || driverData.id || "",
        name: driverData.name || "",
        first_name: driverData.first_name || "",
        last_name: driverData.last_name || "",
        pre: driverData.pre || "",
        gender: driverData.gender || "",
        contact: driverData.contact || driverData.primary_number || "",
        primary_number: driverData.primary_number || driverData.contact || "",
        secondary_number: driverData.secondary_number || "",
        whatsapp_number: driverData.whatsapp_number || "",
        email: driverData.email || "",
        password: "",
        address: driverData.address || "",
        permanent_address: driverData.permanent_address || "",
        city: driverData.city || "",
        state: driverData.state || "",
        postal: driverData.postal || "",
        latitude: driverData.latitude || "",
        longitude: driverData.longitude || "",
        date_of_birth: driverData.date_of_birth || "",
        blood_group: driverData.blood_group || "",
        language: driverData.language || "",
        father_name: driverData.father_name || "",
        heighest_education: driverData.heighest_education || "",
        
        bank_name: driverData.bank_name || "",
        bank_branch: driverData.bank_branch || "",
        account_holder: driverData.account_holder || "",
        account_number: driverData.account_number || "",
        ifsc: driverData.ifsc || "",
        bank_mobile: driverData.bank_mobile || "",
        upi: driverData.upi || "",
        pan_card: driverData.pan_card || "",
        
        status: driverData.status || "",
        paid: driverData.paid || "",
        open_status: driverData.open_status || "",
        is_ready: driverData.is_ready || "",
        hide: driverData.hide || "",
        wallet: driverData.wallet || "",
        cash_inhand: driverData.cash_inhand || "",
        cash_limit: driverData.cash_limit || "",
        cash_limit_cheque: driverData.cash_limit_cheque || "",
        is_cash_limit_cheque: driverData.is_cash_limit_cheque || "",
        tshirt_size: driverData.tshirt_size || "",
        tshirt_given: driverData.tshirt_given || "",
        bag_given: driverData.bag_given || "",
        starting_point: driverData.starting_point || "",
        vl_register_number: driverData.vl_register_number || "",
        dl_number: driverData.dl_number || "",
        
        is_verify_personalinfo: driverData.is_verify_personalinfo || "",
        is_verify_docinfo: driverData.is_verify_docinfo || "",
        is_verify_vehicle: driverData.is_verify_vehicle || "",
        is_verify_bankinfo: driverData.is_verify_bankinfo || "",
        is_verify_emergency: driverData.is_verify_emergency || "",
        remarks_personalinfo: driverData.remarks_personalinfo || "",
        remarks_docinfo: driverData.remarks_docinfo || "",
        remarks_vehicleinfo: driverData.remarks_vehicleinfo || "",
        remarks_bankinfo: driverData.remarks_bankinfo || "",
        remarks_emergencyinfo: driverData.remarks_emergencyinfo || "",
        
        family_number_one: driverData.family_number_one || "",
        family_number_two: driverData.family_number_two || "",
        
        subject: driverData.subject || "",
        details: driverData.details || "",
        refer_by: driverData.refer_by || "",
        myreferal: driverData.myreferal || "",
        created_by_id: driverData.created_by_id || "",
        created_at: driverData.created_at || "",
        updated_at: driverData.updated_at || "",
        rd: driverData.rd || "",
        sessionid: driverData.sessionid || "",
        login_session: driverData.login_session || "",
        fcm_token: driverData.fcm_token || "",
      });
      
      setDocuments(prev => ({
        ...prev,
        pic_existing: driverData.pic || "",
        aadhar_front_existing: driverData.aadhar_front || "",
        aadhar_back_existing: driverData.aadhar_back || "",
        pan_card_existing: driverData.pan_card || "",
        bank_pass_existing: driverData.bank_pass || "",
        cancel_cheque_existing: driverData.cancel_cheque || "",
        dl_front_existing: driverData.dl_front || "",
        dl_back_existing: driverData.dl_back || "",
        rc_front_existing: driverData.rc_front || "",
        rc_back_existing: driverData.rc_back || "",
        resume_existing: driverData.resume || "",
      }));
      
      const allFilled = {};
      Object.keys(filledSections).forEach(key => {
        allFilled[key] = true;
      });
      setFilledSections(allFilled);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    checkSectionFilled(activeSection, { ...formData, [name]: value });
  };

  const handleFileUpload = (docName, file) => {
    setDocuments({ ...documents, [docName]: file });
  };

  const checkSectionFilled = (section, data) => {
    let isFilled = false;
    switch (section) {
      case "contact": isFilled = !!data.contact || !!data.email || !!data.city; break;
      case "personalInfo": isFilled = !!data.first_name || !!data.last_name || !!data.name; break;
      case "documents": isFilled = !!documents.aadhar_front || !!documents.aadhar_back || !!documents.pic; break;
      case "bankDetails": isFilled = !!data.bank_name || !!data.account_number; break;
      case "workDuty": isFilled = !!data.status || !!data.open_status || !!data.dl_number; break;
      case "verification": isFilled = !!data.is_verify_personalinfo || !!data.is_verify_docinfo; break;
      case "familyEmergency": isFilled = !!data.family_number_one; break;
      case "meta": isFilled = !!data.details || !!data.subject; break;
      default: isFilled = false;
    }
    setFilledSections(prev => ({ ...prev, [section]: isFilled }));
  };

  const completedCount = Object.values(filledSections).filter(v => v === true).length;
  const progressPercentage = (completedCount / sections.length) * 100;

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("🚀 Submitting Driver Data:", formData);
    console.log("📎 Documents:", documents);
    alert(isEditMode ? "Driver Updated Successfully!" : "Driver Created Successfully!");
    if (onBack) onBack();
    else navigate(-1);
  };

  const handleCancel = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FILE UPLOAD FIELD WITH PREVIEW
  // ─────────────────────────────────────────────────────────────────────────────
  const FileUploadField = ({ label, docKey }) => {
    const file = documents[docKey];
    const existingFile = documents[`${docKey}_existing`];
    const hasFile = file instanceof File || (existingFile && existingFile !== "");
    
    const getCleanFilename = (picPath) => {
      if (!picPath || picPath.trim() === "" || picPath === "NULL" || picPath === "null") return null;
      if (picPath.includes('/')) return picPath.split('/').pop();
      return picPath;
    };
    
    const filename = getCleanFilename(existingFile);
    const imageUrl = filename ? `${serverURL}/public/uploadnew/upload/${filename}` : null;
    
    return (
      <div style={styles.uploadWrapper}>
        <label style={styles.uploadBox}>
          <input type="file" style={{ display: "none" }} onChange={(e) => handleFileUpload(docKey, e.target.files[0])} />
          <div style={styles.uploadLabel}>
            <UploadIcon />
            <span style={{ color: t.textSecondary, fontSize: "11px", textAlign: "center", wordBreak: "break-all" }}>
              {file instanceof File ? file.name : (existingFile ? existingFile.split('/').pop() : label)}
            </span>
          </div>
        </label>
        
        {/* Show image preview if exists */}
        {(file instanceof File || existingFile) && (
          <div style={styles.previewContainer}>
            {file instanceof File ? (
              <img
                src={URL.createObjectURL(file)}
                alt={label}
                style={styles.previewImage}
              />
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={label}
                style={styles.previewImage}
                onError={(e) => {
                  console.log(`❌ Failed to load preview: ${imageUrl}`);
                  e.target.style.display = 'none';
                }}
              />
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const styles = {
    container: {
      background: isDark ? "#0d1117" : "#f8fafc",
      fontFamily: "'Segoe UI', 'DM Sans', sans-serif",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      padding: "16px 24px",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      background: isDark ? "#0d1117" : "#f8fafc",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    backBtn: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isDark ? "#94a3b8" : "#64748b",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "14px",
      padding: "8px 12px",
      borderRadius: "8px",
    },
    headerTitle: {
      fontSize: "20px",
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#1e293b",
      margin: 0,
      flex: 1,
    },
    progressContainer: {
      padding: "12px 24px",
      background: isDark ? "#0d1117" : "#f8fafc",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    },
    progressHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "8px",
    },
    progressLabel: {
      fontSize: "12px",
      fontWeight: 600,
      color: isDark ? "#64748b" : "#475569",
    },
    progressPercent: {
      fontSize: "12px",
      fontWeight: 700,
      color: "#4a6cf7",
    },
    progressBarBg: {
      background: isDark ? "#1e2740" : "#e2e8f0",
      borderRadius: "10px",
      height: "6px",
      overflow: "hidden",
    },
    progressBarFill: {
      background: "linear-gradient(90deg, #4a6cf7, #8b5cf6)",
      width: `${progressPercentage}%`,
      height: "100%",
      borderRadius: "10px",
      transition: "width 0.3s ease",
    },
    sectionButtons: {
      display: "flex",
      gap: "8px",
      padding: "16px 24px",
      overflowX: "auto",
      background: isDark ? "#0d1117" : "#f8fafc",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      scrollbarWidth: "thin",
    },
    sectionBtn: (isActive, isCompleted) => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
      background: isActive 
        ? "#4a6cf7" 
        : isDark 
          ? "#141824" 
          : "#ffffff",
      border: isActive 
        ? "1px solid #4a6cf7" 
        : isDark 
          ? "1px solid #1e2740" 
          : "1px solid #e2e8f0",
      color: isActive 
        ? "#ffffff" 
        : isDark 
          ? "#94a3b8" 
          : "#64748b",
      whiteSpace: "nowrap",
    }),
    formContainer: {
      flex: 1,
      overflowY: "auto",
      padding: "24px",
    },
    formCard: {
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "16px",
      padding: "24px",
    },
    formTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#1e293b",
      marginBottom: "20px",
      paddingBottom: "12px",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: "20px",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    label: {
      fontSize: "12px",
      fontWeight: 600,
      color: isDark ? "#64748b" : "#475569",
      letterSpacing: "0.3px",
    },
    input: {
      padding: "10px 14px",
      background: isDark ? "#0f1520" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      color: isDark ? "#e2e8f0" : "#1e293b",
      outline: "none",
      transition: "all 0.2s",
    },
    select: {
      padding: "10px 14px",
      background: isDark ? "#0f1520" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      color: isDark ? "#e2e8f0" : "#1e293b",
      outline: "none",
      cursor: "pointer",
    },
    textarea: {
      padding: "10px 14px",
      background: isDark ? "#0f1520" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      color: isDark ? "#e2e8f0" : "#1e293b",
      outline: "none",
      fontFamily: "inherit",
      resize: "vertical",
      minHeight: "80px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: isDark ? "#cbd5e1" : "#334155",
      marginTop: "20px",
      marginBottom: "12px",
      paddingBottom: "6px",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    },
    documentGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: "12px",
    },
    uploadWrapper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
    },
    uploadBox: {
      border: isDark ? "1px dashed #1e2740" : "1px dashed #cbd5e1",
      borderRadius: "10px",
      padding: "12px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      background: isDark ? "#0f1520" : "#f8fafc",
      width: "100%",
      minHeight: "60px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    uploadLabel: {
      fontSize: "12px",
      color: isDark ? "#64748b" : "#94a3b8",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "4px",
      cursor: "pointer",
    },
    previewContainer: {
      width: "80px",
      height: "80px",
      borderRadius: "10px",
      overflow: "hidden",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      flexShrink: 0,
    },
    previewImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    fixedButtons: {
      position: "sticky",
      bottom: 0,
      padding: "16px 24px",
      background: isDark ? "#0d1117" : "#f8fafc",
      borderTop: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      zIndex: 10,
    },
    cancelBtn: {
      padding: "10px 24px",
      background: "transparent",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      color: isDark ? "#94a3b8" : "#64748b",
      cursor: "pointer",
    },
    createBtn: {
      padding: "10px 24px",
      background: "#4a6cf7",
      border: "none",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#fff",
      cursor: "pointer",
    },
    infoRow: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      padding: "12px 0",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    },
    infoLabel: {
      fontSize: "12px",
      fontWeight: 600,
      color: isDark ? "#64748b" : "#475569",
      width: "120px",
    },
    infoValue: {
      fontSize: "13px",
      color: isDark ? "#e2e8f0" : "#1e293b",
      flex: 1,
    },
    navButtons: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: "24px",
      paddingTop: "20px",
      borderTop: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    },
    navBtn: {
      padding: "8px 20px",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      background: isDark ? "#0f1520" : "#f8fafc",
      color: isDark ? "#94a3b8" : "#64748b",
    },
    navBtnPrimary: {
      padding: "8px 20px",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      background: "#4a6cf7",
      border: "none",
      color: "#fff",
    },
  };

  const renderForm = () => {
    switch (activeSection) {
      case "contact":
        return (
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📞 Contact Number *</label>
              <input style={styles.input} name="contact" value={formData.contact} onChange={handleChange} placeholder="+91 98765 43210" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📱 Primary Number</label>
              <input style={styles.input} name="primary_number" value={formData.primary_number} onChange={handleChange} placeholder="Primary contact" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📱 Secondary Number</label>
              <input style={styles.input} name="secondary_number" value={formData.secondary_number} onChange={handleChange} placeholder="Secondary contact" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>💬 WhatsApp</label>
              <input style={styles.input} name="whatsapp_number" value={formData.whatsapp_number} onChange={handleChange} placeholder="WhatsApp number" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✉️ Email *</label>
              <input style={styles.input} name="email" type="email" value={formData.email} onChange={handleChange} placeholder="driver@example.com" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🔒 Password {!isEditMode && "*"}</label>
              <input style={styles.input} name="password" type="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required={!isEditMode} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🏙️ City</label>
              <input style={styles.input} name="city" value={formData.city} onChange={handleChange} placeholder="Enter city" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🗺️ State</label>
              <input style={styles.input} name="state" value={formData.state} onChange={handleChange} placeholder="Enter state" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📍 PIN Code</label>
              <input style={styles.input} name="postal" value={formData.postal} onChange={handleChange} placeholder="6-digit PIN" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🏠 Current Address</label>
              <textarea style={styles.textarea} name="address" value={formData.address} onChange={handleChange} placeholder="Current address" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🏠 Permanent Address</label>
              <textarea style={styles.textarea} name="permanent_address" value={formData.permanent_address} onChange={handleChange} placeholder="Permanent address" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🚩 Starting Point</label>
              <input style={styles.input} name="starting_point" value={formData.starting_point} onChange={handleChange} placeholder="Starting location" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🌐 Latitude</label>
              <input style={styles.input} name="latitude" value={formData.latitude} onChange={handleChange} placeholder="Latitude" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🌐 Longitude</label>
              <input style={styles.input} name="longitude" value={formData.longitude} onChange={handleChange} placeholder="Longitude" />
            </div>
          </div>
        );

      case "personalInfo":
        return (
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👤 Name</label>
              <input style={styles.input} name="name" value={formData.name} onChange={handleChange} placeholder="Full name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👤 First Name</label>
              <input style={styles.input} name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👤 Last Name</label>
              <input style={styles.input} name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👤 Prefix</label>
              <select style={styles.select} name="pre" value={formData.pre} onChange={handleChange}>
                <option value="">Select</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👨 Father's Name</label>
              <input style={styles.input} name="father_name" value={formData.father_name} onChange={handleChange} placeholder="Father's name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🎂 Date of Birth</label>
              <input style={styles.input} name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>⚥ Gender</label>
              <select style={styles.select} name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🩸 Blood Group</label>
              <select style={styles.select} name="blood_group" value={formData.blood_group} onChange={handleChange}>
                <option value="">Select</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🌐 Language</label>
              <input style={styles.input} name="language" value={formData.language} onChange={handleChange} placeholder="Preferred language" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🎓 Highest Education</label>
              <input style={styles.input} name="heighest_education" value={formData.heighest_education} onChange={handleChange} placeholder="e.g., Graduate" />
            </div>
          </div>
        );

      case "documents":
        return (
          <>
            <div style={styles.sectionSubtitle}>📸 Profile Picture</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="Profile Photo" docKey="pic" />
            </div>
            
            <div style={styles.sectionSubtitle}>🆔 Aadhaar Card</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="Aadhaar Front" docKey="aadhar_front" />
              <FileUploadField label="Aadhaar Back" docKey="aadhar_back" />
            </div>
            
            <div style={styles.sectionSubtitle}>📇 PAN Card</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="PAN Card" docKey="pan_card" />
            </div>
            
            <div style={styles.sectionSubtitle}>🚗 Driving License</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="DL Front" docKey="dl_front" />
              <FileUploadField label="DL Back" docKey="dl_back" />
            </div>
            
            <div style={styles.sectionSubtitle}>🚛 Vehicle RC</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="RC Front" docKey="rc_front" />
              <FileUploadField label="RC Back" docKey="rc_back" />
            </div>
            
            <div style={styles.sectionSubtitle}>🏦 Bank Documents</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="Bank Passbook" docKey="bank_pass" />
              <FileUploadField label="Cancel Cheque" docKey="cancel_cheque" />
            </div>
            
            <div style={styles.sectionSubtitle}>📄 Resume</div>
            <div style={styles.documentGrid}>
              <FileUploadField label="Resume" docKey="resume" />
            </div>
          </>
        );

      case "bankDetails":
        return (
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🏦 Bank Name</label>
              <input style={styles.input} name="bank_name" value={formData.bank_name} onChange={handleChange} placeholder="Bank name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📍 Branch</label>
              <input style={styles.input} name="bank_branch" value={formData.bank_branch} onChange={handleChange} placeholder="Branch name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👤 Account Holder</label>
              <input style={styles.input} name="account_holder" value={formData.account_holder} onChange={handleChange} placeholder="Account holder name" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🔢 Account Number</label>
              <input style={styles.input} name="account_number" value={formData.account_number} onChange={handleChange} placeholder="Account number" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🏛️ IFSC</label>
              <input style={styles.input} name="ifsc" value={formData.ifsc} onChange={handleChange} placeholder="IFSC code" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📱 Bank Mobile</label>
              <input style={styles.input} name="bank_mobile" value={formData.bank_mobile} onChange={handleChange} placeholder="Registered mobile" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>💳 UPI</label>
              <input style={styles.input} name="upi" value={formData.upi} onChange={handleChange} placeholder="UPI ID" />
            </div>
            {/* ✅ PAN CARD NUMBER REMOVED - Image will be shown in Documents section */}
          </div>
        );

      case "workDuty":
        return (
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📊 Status</label>
              <select style={styles.select} name="status" value={formData.status} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>💰 Paid</label>
              <select style={styles.select} name="paid" value={formData.paid} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Paid</option>
                <option value="0">Unpaid</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>⏰ Open Status (ON/OFF)</label>
              <select style={styles.select} name="open_status" value={formData.open_status} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">ON</option>
                <option value="0">OFF</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✅ Is Ready</label>
              <select style={styles.select} name="is_ready" value={formData.is_ready} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🙈 Hide</label>
              <select style={styles.select} name="hide" value={formData.hide} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👛 Wallet</label>
              <input style={styles.input} name="wallet" value={formData.wallet} onChange={handleChange} placeholder="Wallet balance" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>💵 Cash in Hand</label>
              <input style={styles.input} name="cash_inhand" value={formData.cash_inhand} onChange={handleChange} placeholder="Cash amount" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>💵 Cash Limit</label>
              <input style={styles.input} name="cash_limit" value={formData.cash_limit} onChange={handleChange} placeholder="Cash limit" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>💵 Cash Limit Cheque</label>
              <input style={styles.input} name="cash_limit_cheque" value={formData.cash_limit_cheque} onChange={handleChange} placeholder="Cheque limit" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👕 T-Shirt Size</label>
              <select style={styles.select} name="tshirt_size" value={formData.tshirt_size} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👕 T-Shirt Given</label>
              <select style={styles.select} name="tshirt_given" value={formData.tshirt_given} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🎒 Bag Given</label>
              <select style={styles.select} name="bag_given" value={formData.bag_given} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🚗 Driving License Number</label>
              <input style={styles.input} name="dl_number" value={formData.dl_number} onChange={handleChange} placeholder="DL Number" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🚛 Vehicle Registration No.</label>
              <input style={styles.input} name="vl_register_number" value={formData.vl_register_number} onChange={handleChange} placeholder="Vehicle number" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>🔗 Referral Code</label>
              <input style={styles.input} name="myreferal" value={formData.myreferal} onChange={handleChange} placeholder="Referral code" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👥 Referred By</label>
              <input style={styles.input} name="refer_by" value={formData.refer_by} onChange={handleChange} placeholder="Referrer name" />
            </div>
          </div>
        );

      case "verification":
        return (
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✅ Personal Verified</label>
              <select style={styles.select} name="is_verify_personalinfo" value={formData.is_verify_personalinfo} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Remarks (Personal)</label>
              <input style={styles.input} name="remarks_personalinfo" value={formData.remarks_personalinfo} onChange={handleChange} placeholder="Remarks" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✅ Documents Verified</label>
              <select style={styles.select} name="is_verify_docinfo" value={formData.is_verify_docinfo} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Remarks (Documents)</label>
              <input style={styles.input} name="remarks_docinfo" value={formData.remarks_docinfo} onChange={handleChange} placeholder="Remarks" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✅ Vehicle Verified</label>
              <select style={styles.select} name="is_verify_vehicle" value={formData.is_verify_vehicle} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Remarks (Vehicle)</label>
              <input style={styles.input} name="remarks_vehicleinfo" value={formData.remarks_vehicleinfo} onChange={handleChange} placeholder="Remarks" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✅ Bank Verified</label>
              <select style={styles.select} name="is_verify_bankinfo" value={formData.is_verify_bankinfo} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Remarks (Bank)</label>
              <input style={styles.input} name="remarks_bankinfo" value={formData.remarks_bankinfo} onChange={handleChange} placeholder="Remarks" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>✅ Emergency Verified</label>
              <select style={styles.select} name="is_verify_emergency" value={formData.is_verify_emergency} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Remarks (Emergency)</label>
              <input style={styles.input} name="remarks_emergencyinfo" value={formData.remarks_emergencyinfo} onChange={handleChange} placeholder="Remarks" />
            </div>
          </div>
        );

      case "familyEmergency":
        return (
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👨‍👩‍👧 Family Number 1</label>
              <input style={styles.input} name="family_number_one" value={formData.family_number_one} onChange={handleChange} placeholder="Emergency contact 1" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>👨‍👩‍👧 Family Number 2</label>
              <input style={styles.input} name="family_number_two" value={formData.family_number_two} onChange={handleChange} placeholder="Emergency contact 2" />
            </div>
          </div>
        );

      case "meta":
        return (
          <>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Subject</label>
              <input style={styles.input} name="subject" value={formData.subject} onChange={handleChange} placeholder="Subject" />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>📝 Details / Remarks</label>
              <textarea style={styles.textarea} name="details" value={formData.details} onChange={handleChange} placeholder="Additional details" />
            </div>
            <div style={{ marginTop: "20px" }}>
              <div style={styles.infoRow}>
                <div style={styles.infoLabel}>🆔 ID</div>
                <div style={styles.infoValue}>{formData.id || "New Driver"}</div>
              </div>
              <div style={styles.infoRow}>
                <div style={styles.infoLabel}>📅 Created At</div>
                <div style={styles.infoValue}>{formData.created_at || "Now"}</div>
              </div>
              <div style={styles.infoRow}>
                <div style={styles.infoLabel}>👤 Created By</div>
                <div style={styles.infoValue}>
                  <input style={styles.input} name="created_by_id" value={formData.created_by_id} onChange={handleChange} placeholder="Admin ID" />
                </div>
              </div>
              <div style={styles.infoRow}>
                <div style={styles.infoLabel}>🔄 Session ID</div>
                <div style={styles.infoValue}>{formData.sessionid || "—"}</div>
              </div>
            </div>
          </>
        );

      default: return null;
    }
  };

  const currentIndex = sections.findIndex(s => s.id === activeSection);
  const isLastSection = currentIndex === sections.length - 1;
  const isFirstSection = currentIndex === 0;

  const goToNext = () => { if (currentIndex < sections.length - 1) setActiveSection(sections[currentIndex + 1].id); };
  const goToPrev = () => { if (currentIndex > 0) setActiveSection(sections[currentIndex - 1].id); };

  return (
    <div style={styles.container}>
      <style>{`
        input::placeholder, textarea::placeholder, select:invalid { color: ${t.placeholder}; opacity: 0.7; }
        input:focus, textarea:focus, select:focus { border-color: ${t.primary}; box-shadow: 0 0 0 3px ${t.primary}20; outline: none; }
        button:hover { transform: translateY(-1px); }
        button:active { transform: translateY(0); }
        [style*="uploadBox"]:hover { border-color: ${t.primary} !important; background: ${t.primary}10 !important; }
        select option { background: ${t.inputBg}; color: ${t.text}; }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={handleCancel}><BackIcon /> Back</button>
        <h2 style={styles.headerTitle}>{isEditMode ? "Edit Driver" : "Add New Driver"}</h2>
        <div style={{ width: "80px" }} />
      </div>

      {/* Progress Meter */}
      <div style={styles.progressContainer}>
        <div style={styles.progressHeader}>
          <span style={styles.progressLabel}>Form Completion</span>
          <span style={styles.progressPercent}>{Math.round(progressPercentage)}%</span>
        </div>
        <div style={styles.progressBarBg}><div style={styles.progressBarFill} /></div>
      </div>

      {/* Section Buttons */}
      <div style={styles.sectionButtons}>
        {sections.map((section) => (
          <button key={section.id} style={styles.sectionBtn(activeSection === section.id, filledSections[section.id])} onClick={() => setActiveSection(section.id)}>
            <span>{section.icon}</span>
            <span>{section.label}</span>
            {filledSections[section.id] && <CheckCircleIcon />}
          </button>
        ))}
      </div>

      {/* Form Container */}
      <div style={styles.formContainer}>
        <div style={styles.formCard}>
          <div style={styles.formTitle}>{sections.find(s => s.id === activeSection)?.label}</div>
          <form onSubmit={handleSubmit}>
            {renderForm()}
            <div style={styles.navButtons}>
              <button type="button" style={styles.navBtn} onClick={goToPrev} disabled={isFirstSection}><ChevronLeftIcon /> Previous</button>
              {!isLastSection ? (
                <button type="button" style={styles.navBtnPrimary} onClick={goToNext}>Next <ChevronRightIcon /></button>
              ) : (
                <button type="submit" style={styles.navBtnPrimary}>{isEditMode ? "Update Driver" : "Create Driver"}</button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Fixed Buttons */}
      <div style={styles.fixedButtons}>
        <button style={styles.cancelBtn} onClick={handleCancel}>Cancel</button>
        <button style={styles.createBtn} onClick={handleSubmit}>{isEditMode ? "Update" : "Create"}</button>
      </div>
    </div>
  );
};

export default AddNewDriver;